import { useState } from "react";
import { toast } from "sonner";
import { ArrowUUpLeft, X } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

const isoOffset = (n) => {
  const d = new Date(); d.setDate(d.getDate() + n);
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
export const fmtShort = (iso) => iso ? new Date(iso + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "numeric", day: "numeric" }) : "";

export function ReturnBadge({ rv, testId }) {
  if (!rv?.date) return null;
  return (
    <span className="text-[9px] font-black uppercase tracking-widest bg-orange-500 text-white px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"
      title={rv.note || "Not finished — returning"} data-testid={testId}>
      ↩ Returning {fmtShort(rv.date)}
    </span>
  );
}

// Inline panel: pick Tomorrow / a date + optional note → POST /jobs/{id}/return-later
export function ReturnLaterPanel({ jobId, onDone, onClose, compact = false }) {
  const [date, setDate] = useState(isoOffset(1));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/return-later`, { date, note });
      toast.success(`↩ Returning ${fmtShort(date)}${r.data.pinned ? " · pinned to your truck" : ""}`);
      onDone?.(r.data);
      onClose?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not schedule return")); }
    finally { setBusy(false); }
  };
  return (
    <div className={`bg-white border-2 border-orange-500 shadow-[4px_4px_0_#f97316] p-3 ${compact ? "w-64" : "w-full"}`} data-testid={`return-later-panel-${jobId}`} onClick={e => e.stopPropagation()}>
      <div className="flex items-center gap-2">
        <ArrowUUpLeft weight="bold" size={16} className="text-orange-600"/>
        <div className="text-[11px] font-black uppercase tracking-widest text-orange-800">Not finished — coming back</div>
        {onClose && <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-900" data-testid={`return-later-close-${jobId}`}><X weight="bold" size={14}/></button>}
      </div>
      <div className="mt-2 flex gap-1">
        {[{ k: isoOffset(1), l: "Tomorrow" }, { k: isoOffset(2), l: fmtShort(isoOffset(2)) }].map(d => (
          <button key={d.k} onClick={() => setDate(d.k)} data-testid={`return-date-${d.l === "Tomorrow" ? "tomorrow" : "day2"}`}
            className={`flex-1 h-9 border-2 text-[10px] font-black uppercase tracking-widest ${date === d.k ? "bg-orange-500 text-white border-orange-600" : "bg-white text-slate-600 border-slate-300"}`}>{d.l}</button>
        ))}
        <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="return-date-input"
          className="h-9 px-1 border-2 border-slate-300 text-[11px] font-bold bg-white w-[38%]"/>
      </div>
      <input value={note} onChange={e => setNote(e.target.value)} placeholder="Why / what's left (optional)" data-testid="return-note-input"
        className="mt-2 w-full h-9 px-2 border-2 border-slate-300 text-xs font-medium"/>
      <button onClick={save} disabled={busy || !date} data-testid={`return-later-save-${jobId}`}
        className="mt-2 w-full h-10 bg-orange-500 hover:bg-orange-600 text-white font-black uppercase text-[11px] tracking-widest disabled:opacity-60">
        {busy ? "Saving…" : `Return ${fmtShort(date)} · keep open`}
      </button>
    </div>
  );
}
